// Light / dark / system appearance preference.
//
// The layout <head> contains a tiny inline script that applies the saved
// preference before first paint (so there is no flash). This module owns the
// rest: persisting changes, following the OS theme while the preference is
// "system", updating <meta name="theme-color">, and wiring any
// [data-theme-option] buttons rendered by ERB views. The React assistant uses
// the same API via window.PromptMailTheme.

export const THEME_STORAGE_KEY = "promptmail:theme";
export const THEME_PREFERENCES = ["system", "light", "dark"];
export const THEME_EVENT = "promptmail:theme";

const THEME_COLORS = { light: "#ffffff", dark: "#212121" };

const systemQuery = () =>
  typeof window.matchMedia === "function" ? window.matchMedia("(prefers-color-scheme: dark)") : null;

export function readThemePreference() {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return THEME_PREFERENCES.includes(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

export function resolveTheme(preference) {
  if (preference === "light" || preference === "dark") return preference;
  return systemQuery()?.matches ? "dark" : "light";
}

function applyTheme(preference) {
  const resolved = resolveTheme(preference);
  const root = document.documentElement;
  root.dataset.theme = resolved;
  root.dataset.themePreference = preference;

  const meta = document.querySelector("meta[name='theme-color']");
  if (meta) meta.setAttribute("content", THEME_COLORS[resolved]);

  document.querySelectorAll("[data-theme-option]").forEach((button) => {
    const active = button.dataset.themeOption === preference;
    button.setAttribute("aria-checked", active ? "true" : "false");
    button.dataset.active = active ? "true" : "false";
  });

  window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: { preference, resolved } }));
  return resolved;
}

export function setThemePreference(preference) {
  const next = THEME_PREFERENCES.includes(preference) ? preference : "system";
  try {
    if (next === "system") {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
    } else {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    }
  } catch {
    // Storage may be unavailable (private mode, disabled cookies); the
    // preference still applies for this page.
  }
  return applyTheme(next);
}

export function getThemePreference() {
  return document.documentElement.dataset.themePreference || readThemePreference();
}

export function getResolvedTheme() {
  return document.documentElement.dataset.theme || resolveTheme(readThemePreference());
}

function handleOptionClick(event) {
  const button = event.target.closest("[data-theme-option]");
  if (!button) return;
  event.preventDefault();
  setThemePreference(button.dataset.themeOption);
}

function initTheme() {
  if (window.PromptMailTheme) return;

  window.PromptMailTheme = {
    preferences: THEME_PREFERENCES,
    get preference() {
      return getThemePreference();
    },
    get resolved() {
      return getResolvedTheme();
    },
    set: setThemePreference,
  };

  applyTheme(readThemePreference());

  systemQuery()?.addEventListener("change", () => {
    if (getThemePreference() === "system") applyTheme("system");
  });

  // Another tab changed the preference.
  window.addEventListener("storage", (event) => {
    if (event.key === THEME_STORAGE_KEY) applyTheme(readThemePreference());
  });

  document.addEventListener("click", handleOptionClick);

  // Turbo swaps <body>, so freshly rendered ERB switches need their state.
  document.addEventListener("turbo:load", () => applyTheme(getThemePreference()));
}

initTheme();
